<template lang="html">
  <div class="show-capsule-container" v-if="currentCapsule">
    <div class="details header">
      <b-container>
        <b-row>
          <b-col>
            <title-bar />
          </b-col>
        </b-row>
      </b-container>
    </div>

    <b-container>
      <b-row>
        <b-col class="pl-0 pr-0">
          <div class="outfit-title-input-details" ref="outfitTitleBar">

            <title-input
              class="input-container"
              :capsule-id="capsuleId"
            />

            <div class="actions-container">
              <b-button-group>
                <b-button
                  v-show="false"
                  class="btn-snippet"
                  variant="link"
                  v-on:click.prevent=""
                  @mouseenter="toggleClipboardText()"
                  @mouseleave="toggleClipboardText()"
                  v-clipboard="generateCodeSnippet()"
                  v-clipboard:success="clipboardSuccessHandler"
                  v-clipboard:error="clipboardErrorHandler"
                >
                  <i :class="clipboardIcon"></i>
                  <br />
                  <span>Snippet</span>
                </b-button>

                <b-button
                  class="btn-shuffle"
                  variant="link"
                  v-on:click="shuffleCapsule"
                >
                  <i class="pe-7s-shuffle"></i>
                  <br />
                  <span>Shuffle</span>
                </b-button>
              </b-button-group>
            </div>
          </div>

          <div class="capsule-row">
            <capsule-content
              :capsule="currentCapsule"
              :stitch-mode="true"
              class="foofoo"
            />
          </div>

          <div class="tabs-section-row" ref="tabsSection">
            <TabsSection />
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import CapsuleContent from "@/components/capsule/CapsuleContent";
import TabsSection from "@/components/showCapsule/TabsSection";
import TitleBar from "@/components/showCapsule/TitleBar";
import TitleInput from "@/components/showCapsule/TitleInput";
import { mapActions, mapGetters } from "vuex";
import { bus } from "@/main";

export default {
  created() {
    this.fetchCurrentCapsule({ capsuleId: this.capsuleId }).then(() => {
      this.populateCapsuleProductIds();
      this.initializeSelectedOutfit();
      this.fetchCapsuleOutfits(this.capsuleId).then(() => {
        this.dataLoaded = true;
        this.checkAndSetOutfit({ layerProducts: this.activeLayerProducts });
      });
    });
  },

  mounted() {
    window.analytics.page("Show Capsule");

    bus.$on("emitActiveProductId", ({ layerSlug, productId }) => {
      if (this.activeLayerProducts[layerSlug] !== productId) {
        this.activeLayerProducts[layerSlug] = productId;

        this.checkAndSetOutfit({ layerProducts: this.activeLayerProducts });
      }
    });
  },

  data() {
    return {
      capsuleId: parseInt(this.$route.params.capsuleId),
      clipboardSuccess: false,
      dataLoaded: false,
      capsuleProductIds: {
        outerwear: null,
        midwear: null,
        innerwear: null,
        bottoms: null,
        footwear: null,
        accessories1: null,
        accessories2: null,
      },
      activeLayerProducts: {
        outerwear: null,
        midwear: null,
        innerwear: null,
        bottoms: null,
        footwear: null,
        accessories1: null,
        accessories2: null,
      },
    };
  },

  beforeDestroy() {
    this.resetCurrentCapsule();
    this.resetSelectedOutfit();
  },

  components: {
    CapsuleContent,
    TabsSection,
    TitleInput,
    TitleBar,
  },

  computed: {
    ...mapGetters(["currentCapsule", "selectedOutfit"]),

    clipboardIcon() {
      return this.clipboardSuccess ? "pe-7s-check" : "pe-7s-note2";
    },

    layerProducts() {
      return this.selectedOutfit.layerProducts;
    },
  },

  methods: {
    ...mapActions([
      "checkAndSetOutfit",
      "fetchCurrentCapsule",
      "fetchCapsuleOutfits",
      "resetCurrentCapsule",
      "resetSelectedOutfit",
    ]),

    initializeSelectedOutfit() {
      Object.keys(this.$route.query).length
        ? this.setActiveLayerProductsWithRouteQuery()
        : this.setActiveLayerProductsWithoutRouteQuery();

      this.checkAndSetOutfit({ layerProducts: this.activeLayerProducts });
    },

    setActiveLayerProductsWithRouteQuery() {
      const query = this.$route.query;

      Object.keys(this.activeLayerProducts).forEach(
        (key) =>
          (this.activeLayerProducts[key] = !query.hasOwnProperty(key)
            ? null
            : this.capsuleProductIds[key].includes(parseInt(query[key]))
            ? parseInt(query[key])
            : this.capsuleProductIds[key][0])
      );
    },

    setActiveLayerProductsWithoutRouteQuery() {
      Object.keys(this.activeLayerProducts).forEach((key) => {
        this.activeLayerProducts[key] = this.capsuleProductIds[key]
          ? this.capsuleProductIds[key][0]
          : null;
      });
    },

    populateCapsuleProductIds() {
      this.currentCapsule.layers.forEach((l) => {
        if (l.layer_products_count > 0) {
          this.capsuleProductIds[l.slug] = this.orderedProductIds(l.products);
        }
      });
    },

    orderedProductIds(products) {
      return products.sort((a, b) => a.sequence - b.sequence).map((p) => p.id);
    },

    getRandomProductId(ids) {
      return ids ? ids[Math.floor(Math.random() * ids.length)] : null;
    },

    buildShuffleCandidate() {
      return Object.entries(this.capsuleProductIds).reduce(
        (a, [k, v]) => ({ ...a, [k]: this.getRandomProductId(v) }),
        {}
      );
    },

    shuffleCapsule() {
      const newOutfit = this.buildShuffleCandidate();

      if (this.compareLayerProducts(newOutfit, this.activeLayerProducts)) {
        this.shuffleCapsule();
      } else {
        this.activeLayerProducts = newOutfit;
        this.checkAndSetOutfit({ layerProducts: this.activeLayerProducts });
      }
    },

    generateCodeSnippet() {
      const generatedUrl = `https://api.unstitched.xyz/capsules/${this.currentCapsule.id}/iframe`;
      const codeSnippet = `<!-- unstitched.xyz capsule -->
  \u003cscript src="https://cdnjs.cloudflare.com/ajax/libs/iframe-resizer/3.6.1/iframeResizer.js"\u003e\u003c/script\u003e
  <div class="unstitched__iframe-wrapper" style="padding-top: 30px;">
    <iframe id="unstitched__iframe_capsule_${this.currentCapsule.id}" src="${generatedUrl}" width="100%" frameBorder="0" scrolling="no" onload="iFrameResize({checkOrigin: false}, '#unstitched__iframe_capsule_${this.currentCapsule.id}')">
      <p>Your browser does not support iframes. Click <a href="${generatedUrl}">here</a> to see this capsule.</p>
    </iframe>
  </div>
<!-- END unstitched.xyz capsule -->
`;

      return codeSnippet;
    },

    toggleClipboardText() {
      this.showClipboardText = !this.showClipboardText;
      this.actionText = this.showClipboardText ? "Copy link to clipboard" : "";
    },

    toggleRemoveText() {
      this.showRemoveText = !this.showRemoveText;
      this.actionText = this.showRemoveText ? "Remove outfit" : "";
    },

    clipboardSuccessHandler({ value, event }) {
      this.clipboardSuccess = true;
      setTimeout(() => (this.clipboardSuccess = false), 1000);
    },

    clipboardErrorHandler({ value, event }) {
      console.log("error", value);
    },

    buildQueryParams() {
      return Object.keys(this.layerProducts)
        .filter((k) => this.layerProducts[k] !== null)
        .reduce((a, k) => ({ ...a, [k]: this.layerProducts[k] }), {});
    },

    compareLayerProducts(objA, objB) {
      return (
        JSON.stringify(Object.values(objA).sort()) ===
        JSON.stringify(Object.values(objB).sort())
      );
    },
  },

  watch: {
    layerProducts: {
      handler: function (newVal, oldVal) {
        if (!this.dataLoaded) return;

        if (!this.compareLayerProducts(newVal, this.activeLayerProducts)) {
          this.activeLayerProducts = newVal;
        }

        const newParams = this.buildQueryParams();
        if (!this.compareLayerProducts(this.$route.query, newParams)) {
          this.$router.replace({ query: newParams }).catch((err) => {});
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_colour-palette.scss";
@import "~@/styles/_breakpoints.scss";

.details {
  text-transform: uppercase;
  padding-top: 35px;
  padding-bottom: 15px;
  margin: -50px -15px 55px;
}

.capsule-row {
  position: relative;
  border: 2px solid transparent;
  padding: 15px;
  margin-right: -15px;
  margin-left: -15px;
}

.tabs-section-row {
  margin-top: 35px;
}

.outfit-title-input-details {
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-start;

  @include for-tablet-portrait-up {
    flex-flow: row nowrap;
    margin-right: -15px;
  }

  .actions-container {
    text-align: center;

    .btn {
      color: $primary-blue;
      text-decoration: none;
      padding-top: 0;

      i {
        font-size: 32px;
        color: $primary-blue;
      }

      &.btn-snippet {
        i {
          font-size: 28px;
          margin-bottom: 6px;
        }
      }

      @media (hover: hover) and (pointer: fine) {
        &:hover {
          i {
            color: $primary-accent;
          }
        }
      }
    }
  }
}

.btn-shuffle {
  &:focus {
    box-shadow: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/showCapsule.vue